import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box,
  Chip,
  CircularProgress,
  Drawer,
  IconButton,
  Slider,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@material-ui/core';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import CloseIcon from '@material-ui/icons/Close';
import AssignmentIcon from '@material-ui/icons/Assignment';
import CampaignIcon from '@mui/icons-material/Campaign';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import VolumeUpIcon from '@material-ui/icons/VolumeUp';
import VolumeOffIcon from '@material-ui/icons/VolumeOff';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';

import NotificationModal from '../NotificationModal';

import { AuthContext } from '../../context/Auth/AuthContext';
import { usePlanPermissions } from '../../context/PlanPermissionsContext.jsx';
import {
  getNotifications,
  getUnreadNotificationCount,
  markAllNotificationsRead,
  markNotificationRead,
} from '../../services/notificationService';
import { isNotificationMuted, playNotificationSound } from '../../utils/notificationSound';
import { isInternalAppUrl, normalizeNotificationUrl } from '../../utils/normalizeNotificationUrl';

const DRAWER_WIDTH = 380;

const useStyles = makeStyles((theme) => ({
  bellButton: (props) => ({
    width: 40,
    height: 40,
    padding: 0,
    borderRadius: '50%',
    color: 'var(--wl-menu-header-icon, var(--wl-menu-icon, #ffffff))',
    backgroundColor: props.primaryColor || '#3b82f6',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s ease',
    '& .MuiSvgIcon-root': {
      fontSize: 20,
      color: 'var(--wl-menu-header-icon, var(--wl-menu-icon, #ffffff))',
    },
    '&:hover': {
      backgroundColor: props.primaryColor || '#2563eb',
      filter: 'brightness(0.9)',
      transform: 'scale(1.05)',
    },
    [theme.breakpoints.down('sm')]: {
      width: 36,
      height: 36,
      '& .MuiSvgIcon-root': { fontSize: 18 },
    },
  }),
  badge: {
    '& .MuiBadge-badge': {
      backgroundColor: '#ef4444',
      color: '#fff',
      fontWeight: 700,
      fontSize: 10,
      minWidth: 16,
      height: 16,
      padding: '0 4px',
    },
  },
  drawerPaper: {
    width: DRAWER_WIDTH,
    display: 'flex',
    flexDirection: 'column',
    borderLeft: '1px solid #e5e7eb',
    boxShadow: '-4px 0 24px rgba(0,0,0,0.08)',
    [theme.breakpoints.down('sm')]: {
      width: '100vw',
    },
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 16px 12px',
    borderBottom: '1px solid #f3f4f6',
    flexShrink: 0,
  },
  headerTitle: {
    fontWeight: 700,
    fontSize: 16,
    color: '#111827',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  volumeSection: {
    padding: '10px 16px',
    borderBottom: '1px solid #f3f4f6',
    flexShrink: 0,
  },
  volumeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  volumeLabel: {
    fontSize: 11,
    color: '#9ca3af',
    marginBottom: 6,
  },
  slider: {
    color: '#4ade80',
    padding: '4px 0',
    flex: 1,
    '& .MuiSlider-track': {
      height: 4,
      borderRadius: 2,
      backgroundColor: '#4ade80',
      border: 'none',
    },
    '& .MuiSlider-rail': { height: 4, borderRadius: 2, backgroundColor: '#dcfce7', opacity: 1 },
    '& .MuiSlider-thumb': {
      width: 13,
      height: 13,
      marginTop: -4.5,
      marginLeft: -6.5,
      backgroundColor: '#fff',
      border: '2px solid #4ade80',
      boxShadow: 'none',
      '&:hover, &.Mui-focusVisible': { boxShadow: '0 0 0 6px rgba(74,222,128,0.18)' },
    },
  },
  volumePercent: {
    fontSize: 11,
    color: '#4ade80',
    fontWeight: 500,
    minWidth: 28,
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
  },
  tabs: {
    flexShrink: 0,
    borderBottom: '1px solid #f3f4f6',
    '& .MuiTab-root': {
      minHeight: 40,
      fontSize: 13,
      fontWeight: 500,
      textTransform: 'none',
      color: '#6b7280',
    },
    '& .Mui-selected': { color: '#111827', fontWeight: 600 },
    '& .MuiTabs-indicator': { backgroundColor: '#3b82f6', height: 2 },
  },
  list: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px 0',
    ...theme.scrollbarStyles,
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: 200,
    gap: 8,
    color: '#9ca3af',
  },
  card: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    padding: '10px 16px',
    cursor: 'pointer',
    transition: 'background-color 0.15s',
    '&:hover': { backgroundColor: '#f9fafb' },
    borderBottom: '1px solid #f3f4f6',
  },
  cardUnread: {
    backgroundColor: '#eff6ff',
    '&:hover': { backgroundColor: '#dbeafe' },
  },
  cardIconWrap: {
    width: 36,
    height: 36,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    marginTop: 2,
  },
  cardContent: {
    flex: 1,
    minWidth: 0,
  },
  cardTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: '#111827',
    lineHeight: 1.4,
    marginBottom: 2,
  },
  cardBody: {
    fontSize: 12,
    color: '#6b7280',
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
  cardImage: {
    width: '100%',
    borderRadius: 6,
    marginBottom: 6,
    maxHeight: 120,
    objectFit: 'cover',
  },
  cardMeta: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  cardTime: {
    fontSize: 11,
    color: '#9ca3af',
  },
  unreadDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    backgroundColor: '#3b82f6',
    flexShrink: 0,
    marginTop: 6,
  },
  loadingWrap: {
    display: 'flex',
    justifyContent: 'center',
    padding: 24,
  },
  pushButton: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 8px',
    marginTop: 8,
    borderRadius: 8,
    border: '1px solid #bbf7d0',
    backgroundColor: '#f0fdf4',
    cursor: 'pointer',
    textAlign: 'left',
    transition: 'background-color 0.15s',
    '&:hover': { backgroundColor: '#dcfce7' },
  },
}));

const TYPE_CONFIG = {
  task: { color: '#3b82f6', bg: '#eff6ff', icon: <AssignmentIcon style={{ fontSize: 18 }} /> },
  announcement: {
    color: '#8b5cf6',
    bg: '#f5f3ff',
    icon: <CampaignIcon style={{ fontSize: 18 }} />,
  },
  opportunity: {
    color: '#10b981',
    bg: '#ecfdf5',
    icon: <AssignmentIcon style={{ fontSize: 18 }} />,
  },
  system: { color: '#6b7280', bg: '#f3f4f6', icon: <CampaignIcon style={{ fontSize: 18 }} /> },
};

const fmtTime = (d) => {
  if (!d) return '';
  const date = new Date(d);
  const now = new Date();
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'agora';
  if (diffMin < 60) return `${diffMin}min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD < 7) return `${diffD}d`;
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
};

const NotificationsDrawer = ({ primaryColor, volume, setVolume }) => {
  const classes = useStyles({ primaryColor });
  const { user, socket } = useContext(AuthContext);
  usePlanPermissions();
  const history = useHistory();
  const historyRef = useRef(history);
  useEffect(() => {
    historyRef.current = history;
  });

  const [open, setOpen] = useState(false);
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const loaded = useRef(false);

  const toggleExpand = (e, id) => {
    e.stopPropagation();
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  // Request browser notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Fetch unread count on mount
  useEffect(() => {
    if (!user?.companyId) return;
    getUnreadNotificationCount()
      .then(({ data }) => setUnreadCount(data.count || 0))
      .catch(() => {});
  }, [user?.companyId]);

  // Load notifications when drawer opens (once)
  const loadNotifications = useCallback(async () => {
    if (loaded.current) return;
    setLoading(true);
    try {
      const { data } = await getNotifications();
      setNotifications(data || []);
      loaded.current = true;
    } catch (_) {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadNotifications();
  }, [open, loadNotifications]);

  const navigateFromNotification = useCallback((notif) => {
    if (notif.resourceType === 'task') {
      historyRef.current.push('/tasks');
      return;
    }
    if (!notif.linkUrl) return;
    const origin = window.location.origin;
    const normalized = normalizeNotificationUrl(notif.linkUrl, origin);
    if (isInternalAppUrl(normalized, origin)) {
      historyRef.current.push(normalized);
    } else {
      window.open(normalized, '_blank');
    }
  }, []);

  // Fire browser notification + sound for incoming socket events
  const fireDesktopNotif = useCallback(
    (notif) => {
      try {
        playNotificationSound();
        if ('Notification' in window && Notification.permission === 'granted') {
          const n = new Notification(notif.title, {
            body: notif.body || '',
            icon: '/favicon.ico',
          });
          n.onclick = (e) => {
            e.preventDefault();
            window.focus();
            navigateFromNotification(notif);
          };
        }
      } catch (_) {
        /* noop */
      }
    },
    [navigateFromNotification]
  );

  // Real-time socket subscription
  useEffect(() => {
    if (!socket?.on || !user?.companyId) return;
    const event = `company${user.companyId}-notification`;
    const onNotif = (data) => {
      if (data.action === 'create') {
        const notif = data.notification;
        // eslint-disable-next-line eqeqeq
        const isForMe =
          (!notif.targetUserId && !notif.targetRole) ||
          // eslint-disable-next-line eqeqeq
          notif.targetUserId == user.id ||
          (notif.targetRole === 'admin' && user.profile === 'admin') ||
          (notif.targetRole === 'user' && user.profile !== 'admin');
        if (!isForMe) return;
        setUnreadCount((prev) => prev + 1);
        setNotifications((prev) =>
          prev.some((n) => n.id === notif.id) ? prev : [{ ...notif, read: false }, ...prev]
        );
        if (document.visibilityState !== 'visible' || !open) {
          fireDesktopNotif(notif);
        }
      }
    };
    socket.on(event, onNotif);
    return () => socket.off(event, onNotif);
  }, [socket, user?.companyId, open, fireDesktopNotif]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const markOneRead = async (e, notif) => {
    e.stopPropagation();
    if (notif.read) return;
    try {
      await markNotificationRead(notif.id);
      setNotifications((prev) => prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n)));
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (_) {
      /* noop */
    }
  };

  const handleCardClick = async (notif) => {
    if (!notif.read) {
      try {
        await markNotificationRead(notif.id);
        setNotifications((prev) => prev.map((n) => (n.id === notif.id ? { ...n, read: true } : n)));
        setUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (_) {
        /* noop */
      }
    }
    if (notif.resourceType === 'task') {
      historyRef.current.push('/tasks');
      handleClose();
    } else if (notif.linkUrl) {
      navigateFromNotification(notif);
      handleClose();
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (_) {
      /* noop */
    }
  };

  const handleVolumeChange = (_, value) => {
    const v = Math.min(1, Math.max(0, Number(value)));
    setVolume(v);
    localStorage.setItem('volume', String(v));
    localStorage.setItem('notificationSoundMuted', v <= 0.01 ? 'true' : 'false');
    if (v > 0.01) playNotificationSound();
  };

  const displayed = tab === 0 ? notifications : notifications.filter((n) => !n.read);

  return (
    <>
      <NotificationModal open={pushModalOpen} onClose={() => setPushModalOpen(false)} />

      <Tooltip title="Notificações">
        <Box style={{ position: 'relative', display: 'inline-flex', flexShrink: 0 }}>
          <IconButton className={classes.bellButton} onClick={handleOpen}>
            <NotificationsActiveIcon />
          </IconButton>
          {unreadCount > 0 && (
            <Box
              style={{
                position: 'absolute',
                top: -4,
                right: -4,
                backgroundColor: '#ef4444',
                color: '#fff',
                borderRadius: 10,
                minWidth: 18,
                height: 18,
                fontSize: 10,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 4px',
                boxSizing: 'border-box',
                border: '2px solid #fff',
                lineHeight: 1,
                zIndex: 1,
                pointerEvents: 'none',
              }}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Box>
          )}
        </Box>
      </Tooltip>

      <Drawer
        anchor="right"
        open={open}
        onClose={handleClose}
        classes={{ paper: classes.drawerPaper }}
      >
        {/* Header */}
        <div className={classes.drawerHeader}>
          <Typography className={classes.headerTitle}>Notificações</Typography>
          <div className={classes.headerActions}>
            <Tooltip title="Marcar todas como lidas">
              <span>
                <IconButton size="small" onClick={handleMarkAllRead} disabled={unreadCount === 0}>
                  <DoneAllIcon
                    style={{ fontSize: 18, color: unreadCount > 0 ? '#3b82f6' : '#d1d5db' }}
                  />
                </IconButton>
              </span>
            </Tooltip>
            <IconButton size="small" onClick={handleClose}>
              <CloseIcon style={{ fontSize: 18, color: '#6b7280' }} />
            </IconButton>
          </div>
        </div>

        {/* Volume */}
        <div className={classes.volumeSection}>
          <Typography className={classes.volumeLabel}>Volume das notificações</Typography>
          <div className={classes.volumeRow}>
            {isNotificationMuted() ? (
              <VolumeOffIcon style={{ fontSize: 16, color: '#9ca3af' }} />
            ) : (
              <VolumeUpIcon style={{ fontSize: 16, color: '#4ade80' }} />
            )}
            <Slider
              className={classes.slider}
              value={Number(volume)}
              step={0.01}
              min={0}
              max={1}
              onChange={handleVolumeChange}
            />
            <Typography className={classes.volumePercent}>{Math.round(volume * 100)}%</Typography>
          </div>
          <button className={classes.pushButton} onClick={() => setPushModalOpen(true)}>
            <NotificationsActiveOutlinedIcon style={{ fontSize: 16, color: '#10b981' }} />
            <Typography style={{ fontSize: 13, color: '#059669', fontWeight: 500 }}>
              Notificações push
            </Typography>
          </button>
        </div>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          className={classes.tabs}
          variant="fullWidth"
        >
          <Tab label="Todas" />
          <Tab
            label={
              <Box display="flex" alignItems="center" style={{ gap: 6 }}>
                Não lidas
                {unreadCount > 0 && (
                  <Chip
                    size="small"
                    label={unreadCount}
                    style={{
                      height: 18,
                      fontSize: 10,
                      fontWeight: 700,
                      backgroundColor: '#ef4444',
                      color: '#fff',
                    }}
                  />
                )}
              </Box>
            }
          />
        </Tabs>

        {/* List */}
        <div className={classes.list}>
          {loading ? (
            <div className={classes.loadingWrap}>
              <CircularProgress size={24} />
            </div>
          ) : displayed.length === 0 ? (
            <div className={classes.empty}>
              <NotificationsActiveIcon style={{ fontSize: 40, color: '#d1d5db' }} />
              <Typography style={{ fontSize: 13, color: '#9ca3af' }}>
                {tab === 1 ? 'Nenhuma notificação não lida' : 'Nenhuma notificação'}
              </Typography>
            </div>
          ) : (
            displayed.map((notif) => {
              const cfg = TYPE_CONFIG[notif.type] || TYPE_CONFIG.system;
              const isExpanded = expandedIds.has(notif.id);
              const isLong = notif.body && notif.body.length > 60;
              return (
                <div
                  key={notif.id}
                  className={`${classes.card} ${!notif.read ? classes.cardUnread : ''}`}
                  onClick={() => handleCardClick(notif)}
                >
                  {/* Icon */}
                  <div
                    className={classes.cardIconWrap}
                    style={{ backgroundColor: cfg.bg, color: cfg.color }}
                  >
                    {React.cloneElement(cfg.icon, { style: { fontSize: 18, color: cfg.color } })}
                  </div>

                  {/* Content */}
                  <div className={classes.cardContent}>
                    {notif.imageUrl && (
                      <img src={notif.imageUrl} alt="" className={classes.cardImage} />
                    )}
                    <Typography className={classes.cardTitle}>{notif.title}</Typography>
                    {notif.body && (
                      <>
                        <Typography
                          className={classes.cardBody}
                          style={
                            isExpanded
                              ? { display: 'block', overflow: 'visible', WebkitLineClamp: 'unset' }
                              : {}
                          }
                        >
                          {notif.body}
                        </Typography>
                        {isLong && (
                          <Typography
                            onClick={(e) => toggleExpand(e, notif.id)}
                            style={{
                              fontSize: 11,
                              color: '#3b82f6',
                              cursor: 'pointer',
                              marginTop: 2,
                              userSelect: 'none',
                            }}
                          >
                            {isExpanded ? 'Ver menos' : 'Ver mais'}
                          </Typography>
                        )}
                      </>
                    )}
                    {notif.linkUrl && (
                      <Box display="flex" alignItems="center" style={{ gap: 3, marginTop: 4 }}>
                        <OpenInNewIcon style={{ fontSize: 11, color: '#3b82f6' }} />
                        <Typography style={{ fontSize: 11, color: '#3b82f6' }}>
                          {notif.linkLabel || 'Ver mais'}
                        </Typography>
                      </Box>
                    )}
                    <div className={classes.cardMeta}>
                      <Typography className={classes.cardTime}>
                        {fmtTime(notif.createdAt)}
                      </Typography>
                    </div>
                  </div>

                  {/* Unread indicator + mark as read button */}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 4,
                      flexShrink: 0,
                    }}
                  >
                    {!notif.read && (
                      <>
                        <div className={classes.unreadDot} />
                        <Tooltip title="Marcar como lida">
                          <IconButton
                            size="small"
                            onClick={(e) => markOneRead(e, notif)}
                            style={{ padding: 2 }}
                          >
                            <CheckCircleOutlineIcon style={{ fontSize: 14, color: '#3b82f6' }} />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Drawer>
    </>
  );
};

export default NotificationsDrawer;
